import { Link } from "react-router-dom";
import { formatHeat, type GossipArticle } from "@/utils/gossip";

interface ArticleCardProps {
  article: GossipArticle;
  variant?: "default" | "feature" | "compact";
  showExcerpt?: boolean;
}

export default function ArticleCard({
  article,
  variant = "default",
  showExcerpt = true,
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <article className="group flex gap-4 py-4 border-b border-background-200 last:border-0">
        <Link
          to={`/hot/${article.id}`}
          className="w-24 h-20 md:w-28 md:h-20 shrink-0 rounded-lg overflow-hidden cursor-pointer"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title}配图`}
            title={`${article.title} - 看料吃瓜`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="min-w-0 flex flex-col justify-center gap-1.5">
          <Link
            to={`/hot?category=${article.category}`}
            className="text-[11px] font-label text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full w-fit hover:bg-primary-200 transition-colors"
          >
            {article.categoryLabel}
          </Link>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-2">
            <Link to={`/hot/${article.id}`} className="hover:text-primary-700 transition-colors cursor-pointer">
              {article.title}
            </Link>
          </h3>
          <div className="flex items-center gap-3 text-[11px] font-label text-foreground-500">
            <span>{article.date}</span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatHeat(article.heat)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  const isFeature = variant === "feature";

  return (
    <article className="group flex flex-col h-full bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link
        to={`/hot/${article.id}`}
        className={`block w-full overflow-hidden ${isFeature ? "h-64 md:h-80" : "h-48"} cursor-pointer`}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title}配图`}
          title={`${article.title} - 看料吃瓜`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </Link>
      <div className={`flex flex-col flex-1 ${isFeature ? "p-5 md:p-6" : "p-4 md:p-5"}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Link
            to={`/hot?category=${article.category}`}
            className="text-[11px] font-label text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full hover:bg-primary-200 transition-colors"
          >
            {article.categoryLabel}
          </Link>
          {article.hot && (
            <span className="text-[11px] font-label text-accent-900 bg-accent-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <i className="ri-fire-line"></i>
              热
            </span>
          )}
          {article.deep && (
            <span className="text-[11px] font-label text-secondary-900 bg-secondary-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <i className="ri-focus-3-line"></i>
              深水
            </span>
          )}
        </div>

        <h3
          className={`font-heading font-semibold text-foreground-950 leading-snug ${
            isFeature ? "text-xl md:text-2xl mb-3" : "text-base md:text-lg mb-2"
          }`}
        >
          <Link to={`/hot/${article.id}`} className="hover:text-primary-700 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>

        {showExcerpt && (
          <p
            className={`text-foreground-600 leading-relaxed ${
              isFeature ? "text-sm mb-4 line-clamp-3" : "text-xs md:text-sm mb-4 line-clamp-2"
            }`}
          >
            {article.excerpt}
          </p>
        )}

        <div className="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-background-200">
          <span className="text-[11px] font-label text-foreground-500 inline-flex items-center gap-1.5 min-w-0">
            <i className="ri-user-3-line"></i>
            <span className="truncate">{article.author}</span>
          </span>
          <span className="flex items-center gap-3 text-[11px] font-label text-foreground-500 shrink-0">
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="inline-flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line"></i>
              {formatHeat(article.heat)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}